import type { QueryClient } from '@tanstack/react-query';

import { DISCOVER_TRACK_KEY } from '@/constants/queryKeys';
import type { RecommendedTracksResponse } from '@/types';

/**
 * discover-card-polish — « +1 réponse sur cette reco », écrit dans le cache.
 *
 * comment-circle-scope — l'incrément est SCOPÉ AU CERCLE. Une conversation
 * appartient à un cercle : la même reco porte un compteur différent dans
 * chacun, et les confondre affichait sur l'onglet Racore le nombre de réponses
 * écrites dans un cercle privé.
 *
 * La pastille lit `commentCount` sur les données du feed. Envoyer une réponse
 * mettait à jour la feuille (`setThread`) et jamais ce cache : il fallait
 * recharger pour voir le chiffre bouger.
 *
 * ÉCRITURE DIRECTE, jamais `invalidateQueries`. Le feed est un
 * `useInfiniteQuery` : l'invalider refetcherait TOUTES les pages chargées, et
 * `notificationInvalidation.ts` porte déjà l'avertissement — un refetch général
 * « rebattrait le deck sous le doigt de l'utilisateur ». Payer ça pour
 * incrémenter un nombre serait disproportionné.
 *
 * Deux appelants : l'envoi local, et la réception d'un push `reply_received`.
 * Une seule fonction, pour que les deux chemins ne puissent pas diverger — tous
 * deux connaissent le cercle (le hook le porte, le push le reçoit du serveur).
 */
export function bumpCommentCount(
  queryClient: QueryClient,
  recommendationId: number,
  groupId: number | undefined,
): void {
  // Sans cercle, on s'abstient. Une charge utile de push antérieure au champ
  // `groupId` laisse le chiffre vieux jusqu'au prochain chargement — ce qui vaut
  // mieux que juste dans le mauvais cercle. Le paramètre est REQUIS (quitte à
  // valoir `undefined`) pour qu'un appelant qui l'oublie ne compile pas.
  if (groupId == null) return;
  // Toutes les entrées `discover-track` DU MÊME CERCLE, pas seulement la query
  // active : la clé est `['discover-track', selectedGroupId, includePublic,
  // styles]`, donc la même reco vit dans plusieurs entrées qui partagent le même
  // compteur. Sur les styles et le mode public il faut balayer — n'en toucher
  // qu'une laisserait le chiffre faux dès un changement de filtre. Sur le
  // cercle, jamais : c'est une autre conversation.
  queryClient.setQueriesData<{ pages: RecommendedTracksResponse[]; pageParams: unknown[] }>(
    // Le filtre est un PRÉFIXE de clé : `['discover-track', 7]` retient toutes
    // les entrées du cercle 7 quels que soient le mode public et les styles, et
    // aucune d'un autre cercle. C'est react-query qui fait le tri — l'updater ne
    // reçoit pas sa query, et n'a pas à la recevoir.
    { queryKey: [...DISCOVER_TRACK_KEY, groupId] },
    (cached) => {
      if (!cached?.pages) return cached;

      let touched = false;
      const pages = cached.pages.map((page) => {
        if (!page?.data?.some((reco) => reco.id === recommendationId)) return page;
        touched = true;
        return {
          ...page,
          data: page.data.map((reco) =>
            reco.id === recommendationId
              ? { ...reco, commentCount: (reco.commentCount ?? 0) + 1 }
              : reco,
          ),
        };
      });

      // Rendre l'objet d'origine quand rien n'a bougé : une nouvelle référence
      // ferait re-rendre tout le deck pour rien.
      return touched ? { ...cached, pages } : cached;
    },
  );
}
